/**
 * Page Translator Service
 * Translates all text content on a page using Azure Translator
 */

import azureTranslatorService from './azureTranslatorService';

class PageTranslatorService {
  constructor() {
    this.translationCache = new Map();
  }

  /**
   * Get cache key for page translations
   */
  getPageCacheKey(route, language) {
    return `page-translations-${route}-${language}`;
  }

  /**
   * Save page translations to localStorage
   */
  savePageTranslations(route, language, translations) {
    try {
      const cacheKey = this.getPageCacheKey(route, language);
      // Convert Map to array for JSON serialization
      const translationsArray = Array.from(translations.entries());
      localStorage.setItem(cacheKey, JSON.stringify(translationsArray));
      console.log(`[Page Translator] Saved ${translations.size} translations for ${route} in ${language}`);
    } catch (error) {
      console.error('[Page Translator] Error saving page translations:', error);
    }
  }

  /**
   * Load page translations from localStorage
   */
  loadPageTranslations(route, language) {
    try {
      const cacheKey = this.getPageCacheKey(route, language);
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        // Convert array back to Map
        const translationsArray = JSON.parse(cached);
        const translations = new Map(translationsArray);
        console.log(`[Page Translator] Loaded ${translations.size} cached translations for ${route} in ${language}`);
        return translations;
      }
    } catch (error) {
      console.error('[Page Translator] Error loading page translations:', error);
    }
    return null;
  }

  /**
   * Apply cached translations to page
   */
  applyCachedTranslations(cachedTranslations) {
    if (!cachedTranslations || cachedTranslations.size === 0) {
      return false;
    }

    let appliedCount = 0;
    const textNodes = this.extractTextNodes(document.body);

    textNodes.forEach(node => {
      const originalText = node.textContent?.trim();
      if (originalText && this.shouldTranslateText(originalText)) {
        const translatedText = cachedTranslations.get(originalText);
        if (translatedText && translatedText !== originalText) {
          node.textContent = translatedText;
          appliedCount++;
        }
      }
    });

    // Also apply to attributes
    const elementsWithAttributes = Array.from(document.body.querySelectorAll('input, textarea, img, [title], [alt], [aria-label], [placeholder]'))
      .filter(el => !el.closest('[data-no-translate]') && !el.closest('.no-translate'));

    elementsWithAttributes.forEach(element => {
      ['title', 'alt', 'aria-label', 'placeholder'].forEach(attr => {
        const originalValue = element.getAttribute(attr);
        if (originalValue && this.shouldTranslateText(originalValue)) {
          const translatedValue = cachedTranslations.get(originalValue);
          if (translatedValue && translatedValue !== originalValue) {
            element.setAttribute(attr, translatedValue);
            appliedCount++;
          }
        }
      });
    });

    console.log(`[Page Translator] Applied ${appliedCount} cached translations`);
    return appliedCount > 0;
  }

  shouldTranslateText(text) {
    if (!text || text.trim() === '') return false;
    // Skip numbers, URLs, emails
    if (/^\d+$/.test(text.trim())) return false;
    if (/^https?:\/\//.test(text.trim())) return false;
    if (/^[\w.-]+@[\w.-]+\.\w+$/.test(text.trim())) return false;
    // Skip if already very short (likely not meaningful)
    if (text.trim().length < 2) return false;
    return true;
  }

  extractTextNodes(element) {
    const textNodes = [];
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          // Skip script and style tags
          if (node.parentElement?.tagName === 'SCRIPT' || 
              node.parentElement?.tagName === 'STYLE') {
            return NodeFilter.FILTER_REJECT;
          }
          // Skip if marked as no-translate
          if (node.parentElement?.closest('[data-no-translate]') ||
              node.parentElement?.closest('.no-translate')) {
            return NodeFilter.FILTER_REJECT;
          }
          // IMPORTANT: Include dropdown menus (category-dropdown-menu)
          // These are dynamically rendered and need translation
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    let node;
    while (node = walker.nextNode()) {
      if (node.textContent?.trim()) {
        textNodes.push(node);
      }
    }

    return textNodes;
  }

  async translateAttributes(element, targetLanguage, sourceLanguage) {
    const attributes = ['title', 'alt', 'aria-label', 'placeholder'];
    const textsToTranslate = [];
    const attrMap = [];

    attributes.forEach(attr => {
      const value = element.getAttribute(attr);
      if (value && this.shouldTranslateText(value)) {
        textsToTranslate.push(value);
        attrMap.push(attr);
      }
    });

    if (textsToTranslate.length === 0) return;

    try {
      const translatedTexts = await azureTranslatorService.translateBatch(
        textsToTranslate,
        targetLanguage,
        sourceLanguage
      );

      attrMap.forEach((attr, index) => {
        if (translatedTexts[index]) {
          element.setAttribute(attr, translatedTexts[index]);
        }
      });
    } catch (error) {
      console.error('[Page Translator] Error translating attributes:', error);
    }
  }

  async translatePage(targetLanguage, sourceLanguage = 'en') {
    if (targetLanguage === sourceLanguage || targetLanguage === 'en') {
      this.clearCache();
      return Promise.resolve();
    }

    const currentRoute = window.location.pathname + window.location.search;
    
    // Check if we have cached translations for this page
    const cachedTranslations = this.loadPageTranslations(currentRoute, targetLanguage);
    if (cachedTranslations) {
      console.log(`[Page Translator] Found cached translations for ${currentRoute} in ${targetLanguage}, applying...`);
      const applied = this.applyCachedTranslations(cachedTranslations);
      if (applied) {
        // Wait a bit for DOM to settle, then apply again to catch any dynamic content
        setTimeout(() => {
          this.applyCachedTranslations(cachedTranslations);
        }, 500);
        return Promise.resolve();
      }
    }

    console.log(`[Page Translator] Translating page to ${targetLanguage}...`);

    const hasSubParams = window.location.search.includes('sub=') || 
                         window.location.search.includes('subsub=');
    const isSubcategoryPage = hasSubParams || window.location.pathname.includes('/category/');
    const isSubcategoryProductPage = hasSubParams;
    const isProductDetailPage = window.location.pathname.startsWith('/product/');
    const isProductsPage = window.location.pathname === '/products';

    // Reduce initial wait times for faster translation
    let initialWait;
    if (isProductDetailPage || isProductsPage) {
      initialWait = 200; // Fast for product pages
    } else if (isSubcategoryProductPage) {
      initialWait = 600; // Still need time for dynamic content
    } else if (isSubcategoryPage) {
      initialWait = 400;
    } else {
      initialWait = 100; // Very fast for regular pages
    }
    await new Promise(resolve => setTimeout(resolve, initialWait));

    let mainContent = document.body;
    if (!mainContent) {
      await new Promise(resolve => setTimeout(resolve, isSubcategoryPage ? 1000 : 300));
      mainContent = document.body;
      if (!mainContent) return Promise.resolve();
    }

    let nodesToTranslate = [];
    let retryCount = 0;
    
    // For product detail pages and regular pages, translate immediately
    if (isProductDetailPage || isProductsPage) {
      // Product pages - translate immediately, don't wait for retries
      const textNodes = this.extractTextNodes(mainContent);
      nodesToTranslate = textNodes.filter(node => {
        const text = node.textContent?.trim();
        return this.shouldTranslateText(text);
      });
      console.log(`[Page Translator] Product page detected - Found ${nodesToTranslate.length} text nodes, translating immediately`);
    } else if (isSubcategoryPage) {
      // Subcategory pages still need retry logic for dynamic content
      const maxRetries = isSubcategoryProductPage ? 12 : 10;
      const minTextNodes = isSubcategoryProductPage ? 60 : 50;
      
      while (retryCount < maxRetries) {
        const textNodes = this.extractTextNodes(mainContent);
        nodesToTranslate = textNodes.filter(node => {
          const text = node.textContent?.trim();
          return this.shouldTranslateText(text);
        });

        console.log(`[Page Translator] Retry ${retryCount + 1}/${maxRetries}: Found ${nodesToTranslate.length} text nodes`);

        if (nodesToTranslate.length >= minTextNodes) {
          console.log(`[Page Translator] ✅ Sufficient content loaded`);
          break;
        }

        await new Promise(resolve => setTimeout(resolve, isSubcategoryProductPage ? 1000 : 800));
        retryCount++;
      }

      if (nodesToTranslate.length < 50) {
        await new Promise(resolve => setTimeout(resolve, isSubcategoryProductPage ? 1500 : 1000));
        const finalTextNodes = this.extractTextNodes(mainContent);
        nodesToTranslate = finalTextNodes.filter(node => {
          const text = node.textContent?.trim();
          return this.shouldTranslateText(text);
        });
      }
    } else {
      // Regular pages - translate immediately
      const textNodes = this.extractTextNodes(mainContent);
      nodesToTranslate = textNodes.filter(node => {
        const text = node.textContent?.trim();
        return this.shouldTranslateText(text);
      });
      console.log(`[Page Translator] Regular page - Found ${nodesToTranslate.length} text nodes, translating immediately`);
    }

    const elementsWithAttributes = Array.from(mainContent.querySelectorAll('input, textarea, img, [title], [alt], [aria-label]'))
      .filter(el => !el.closest('[data-no-translate]') && !el.closest('.no-translate'));

    console.log(`[Page Translator] Found ${nodesToTranslate.length} text nodes and ${elementsWithAttributes.length} elements`);

    const batchSize = 50;
    let translatedCount = 0;
    const translationMap = new Map(); // Store original -> translated mapping

    for (let i = 0; i < nodesToTranslate.length; i += batchSize) {
      const batch = nodesToTranslate.slice(i, i + batchSize);
      // Capture original texts BEFORE translation
      const originalTexts = batch.map(node => {
        const text = node.textContent.trim();
        return this.shouldTranslateText(text) ? text : null;
      }).filter(text => text !== null);
      
      if (originalTexts.length === 0) continue;

      try {
        const translatedTexts = await azureTranslatorService.translateBatch(
          originalTexts,
          targetLanguage,
          sourceLanguage
        );

        // Apply translations and store mapping
        let textIndex = 0;
        batch.forEach((node) => {
          const originalText = node.textContent.trim();
          if (this.shouldTranslateText(originalText) && textIndex < translatedTexts.length) {
            const translatedText = translatedTexts[textIndex];
            if (translatedText && translatedText !== originalText) {
              node.textContent = translatedText;
              translationMap.set(originalText, translatedText);
              translatedCount++;
            }
            textIndex++;
          }
        });

        if (i + batchSize < nodesToTranslate.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error(`[Page Translator] Error translating batch:`, error);
      }
    }

    let attributeCount = 0;
    for (const element of elementsWithAttributes) {
      try {
        await this.translateAttributes(element, targetLanguage, sourceLanguage);
        attributeCount++;
        await new Promise(resolve => setTimeout(resolve, 10));
      } catch (error) {
        console.error('[Page Translator] Error translating attributes:', error);
      }
    }

    // Cache attribute translations
    for (const element of elementsWithAttributes) {
      ['title', 'alt', 'aria-label', 'placeholder'].forEach(attr => {
        const originalValue = element.getAttribute(attr);
        if (originalValue && this.shouldTranslateText(originalValue)) {
          // Attribute translations are already applied, get the translated value
          const translatedValue = element.getAttribute(attr);
          if (translatedValue && translatedValue !== originalValue && !translationMap.has(originalValue)) {
            translationMap.set(originalValue, translatedValue);
          }
        }
      });
    }

    console.log(`[Page Translator] ✅ Translated ${translatedCount} text nodes and ${attributeCount} attributes`);

    // Save translations to localStorage for future visits
    if (translationMap.size > 0) {
      this.savePageTranslations(currentRoute, targetLanguage, translationMap);
    }

    // Set up MutationObserver for late-loading content (subcategory pages AND dropdown menus)
    // This catches dynamically rendered content like category dropdowns
    const mutationObserver = new MutationObserver((mutations) => {
      const additionalNodes = [];
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // Check if this is a dropdown menu (category-dropdown-menu)
          const isDropdown = node.nodeType === Node.ELEMENT_NODE && 
                            (node.classList?.contains('category-dropdown-menu') ||
                             node.querySelector?.('.category-dropdown-menu') ||
                             node.closest?.('.category-dropdown-menu'));
          
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent?.trim();
            if (text && this.shouldTranslateText(text)) {
              additionalNodes.push(node);
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            // Extract text nodes from the added element
            const textNodes = this.extractTextNodes(node);
            textNodes.forEach((textNode) => {
              const text = textNode.textContent?.trim();
              if (text && this.shouldTranslateText(text)) {
                additionalNodes.push(textNode);
              }
            });
          }
        });
      });

      if (additionalNodes.length > 0) {
        (async () => {
          try {
            console.log(`[Page Translator] Translating ${additionalNodes.length} dynamically added nodes (dropdowns, etc.)`);
            const translatedTexts = await azureTranslatorService.translateBatch(
              additionalNodes.map(node => node.textContent.trim()),
              targetLanguage,
              sourceLanguage
            );
            
            additionalNodes.forEach((node, index) => {
              if (index < translatedTexts.length && translatedTexts[index]) {
                const originalText = node.textContent.trim();
                const translatedText = translatedTexts[index];
                if (translatedText !== originalText) {
                  node.textContent = translatedText;
                }
              }
            });
            console.log(`[Page Translator] ✅ Translated ${additionalNodes.length} dynamic nodes`);
          } catch (error) {
            console.error('[Page Translator] Error translating additional nodes:', error);
          }
        })();
      }
    });

    // Observe for dropdown menus and dynamic content
    // Keep observing longer to catch dropdown menus that open on hover
    mutationObserver.observe(mainContent, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // Keep observer active longer to catch dropdown menus
    setTimeout(() => {
      mutationObserver.disconnect();
      console.log('[Page Translator] MutationObserver disconnected');
    }, 10000); // 10 seconds to catch dropdown menus

    return Promise.resolve();
  }

  clearCache() {
    this.translationCache.clear();
  }

  /**
   * Clear page translation cache for a specific route and language
   */
  clearPageCache(route, language) {
    try {
      const cacheKey = this.getPageCacheKey(route, language);
      localStorage.removeItem(cacheKey);
      console.log(`[Page Translator] Cleared cache for ${route} in ${language}`);
    } catch (error) {
      console.error('[Page Translator] Error clearing page cache:', error);
    }
  }
}

const pageTranslatorService = new PageTranslatorService();
export default pageTranslatorService;

